import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import './main.css'
import Cookies from 'js-cookie';
import { useTranslation } from 'react-i18next';
import ReactCountryFlag from "react-country-flag";
import { useTheme } from '../../themeContext';
import { AbilityProvider, useAbility } from '../../roles/AbilityContext';
import Logo1 from '../../components/image/Logo 1.png';
import Logo2 from '../../components/image/Logo 2.png';
import Logo3 from '../../components/image/Logo 3.png';
import Logo4 from '../../components/image/Logo 4.svg';
import Footer1 from '../../components/image/Footer 1.png';
import Footer2 from '../../components/image/Footer 2.png';
import Footer3 from '../../components/image/Footer 3.png';
import { getAlertype, getCompany, getConstant, getProfile, getStatus, getVessel } from '../../services/requests';
import { toast } from 'sonner';
import { ClipLoader } from "react-spinners";
import PhoneInput from 'react-phone-number-input';
import axios from 'axios';
import { patchUpdateUser } from '../../services/requests';
import { Company, UserData, Vessels } from '../../interfaces/interfaces';

const PreMain = () => {
    const userRole = Cookies.get('role');

    return (
        <AbilityProvider role={userRole}>
            <Main />
        </AbilityProvider>
    );
};

const Main: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const { toggleTheme } = useTheme();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [darkMode, setDarkMode] = useState<boolean>(() => {
        const savedTheme = Cookies.get('dark-theme');
        return savedTheme === 'true';
    });
    const userFullName = Cookies.get('name');
    const userInitial = userFullName.charAt(0);
    const userLastInitial = userFullName.split(' ')[1]?.charAt(0) || '';
    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(() => {
        const savedTheme = Cookies.get('menu-open');
        return savedTheme === 'true';
    });
    const menuRef = useRef<HTMLDivElement>(null);
    const [isMenuUserOpen, setIsMenuUserOpen] = useState<boolean>(false);
    const menuUserRef = useRef<HTMLDivElement>(null);
    const [isMenuLanguageOpen, setIsMenuLanguageOpen] = useState<boolean>(false);
    const menuLanguageRef = useRef<HTMLDivElement>(null);
    const ability = useAbility();
    const location = useLocation();
    const [showRotateWarning, setShowRotateWarning] = useState(false);
    const userRole = Cookies.get('roleName');
    const [dataVessel, setDataVessel] = useState<Vessels[]>([]);
    const [dataCompany, setDataCompany] = useState<Company[]>([]);
    const [dataProfile, setDataProfile] = useState<UserData[]>([]);
    const nempresa = Cookies.get('Nempresa');
    const idUser = Cookies.get('idUser');

    /**
     * Obtiene información inicial necesaria para el funcionamiento del sistema.
     * - Llama a los servicios: `getAlertype`, `getCompany`, `getConstant`, `getStatus`, `getVessel`.
     * - Muestra mensajes de éxito con `toast` si cada llamada responde correctamente.
     * - Aplica retrasos con `setTimeout` en algunos `toast` para evitar saturación visual.
     * - Al finalizar, guarda una cookie `start-info` con valor `"0"`.
     * - Si ocurre un error en cualquier llamada, muestra un mensaje de error con `toast` y desactiva el estado de carga.
     *
     * Esta función está memorizada con `useCallback` para evitar recreaciones innecesarias.
     */
    const getInformation = useCallback(async () => {

        try {
            const responseAlertype = await getAlertype();

            const responseCompany = await getCompany();

            const filteredCompany = (responseCompany.data.data as Company[]).filter(company => nempresa === '' || company.Nempresa === Number(nempresa))
            setDataCompany(filteredCompany);

            const responseConstant = await getConstant();

            const responseStatus = await getStatus();

            const responseVessel = await getVessel();

            const filteredVessel = (responseVessel.data.data as Vessels[]).filter(vessel => nempresa === '' || vessel.Nempresa === Number(nempresa))
            setDataVessel(filteredVessel);

            const allOk = [responseAlertype, responseCompany, responseConstant, responseStatus, responseVessel]
                .every(res => res.status >= 200 && res.status < 300);

            if (allOk) {
                toast.success(
                    `${responseAlertype.data.message}, ${responseCompany.data.message}, ${responseConstant.data.message}, ${responseStatus.data.message}, ${responseVessel.data.message}`
                );
            }

            const responseProfile = await getProfile(Cookies.get('token'));

            const transformedData = (responseProfile.data.data as UserData[]).map((item) => ({
                id: item.id,
                Tcontacto: item.Tcontacto,
                user: item.user,
                Nempresa: item.Nempresa,
                Nembarcacion: item.Nembarcacion || null,
                Rol: item.Rol
            }));

            setDataProfile(transformedData);

            Cookies.set('start-info', `0`);

        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || error;
            toast.error(`${errorMessage}`);
            setLoading(false);
        } finally {
            setLoading(false);
        }
    }, []);

    const getInformationProfile = useCallback(async () => {

        try {
            const responseProfile = await getProfile(Cookies.get('token'));

            const transformedData = (responseProfile.data.data as UserData[]).map((item) => ({
                id: item.id,
                Tcontacto: item.Tcontacto,
                user: item.user,
                Nempresa: item.Nempresa,
                Nembarcacion: item.Nembarcacion || null,
                Rol: item.Rol
            }));

            setDataProfile(transformedData);

            const responseCompany = await getCompany();

            const filteredCompany = (responseCompany.data.data as Company[]).filter(company => nempresa === '' || company.Nempresa === Number(nempresa))
            setDataCompany(filteredCompany);

            const responseVessel = await getVessel();

            const filteredVessel = (responseVessel.data.data as Vessels[]).filter(vessel => nempresa === '' || vessel.Nempresa === Number(nempresa))
            setDataVessel(filteredVessel);

        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || error;
            toast.error(`${errorMessage}`);
            setLoading(false);
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Ejecuta la función `getInformation` al montar el componente
     * o cuando cambien las dependencias de `getInformation`.
     */
    useEffect(() => {
        if (Cookies.get('start-info') === '1') {
            getInformation();
        } else {
            getInformationProfile();
        }
    }, [getInformation]);

    /**
     * Cierra el menú lateral si el ancho de la ventana es menor a 1201 píxeles.
     */
    const closeAllSubmenu = () => {

        if (window.innerWidth < 1201) {
            setIsMenuOpen(false);
        }
    };

    /**
     * Alterna el modo de pantalla completa del navegador.
     * Si no está en pantalla completa, la activa.
     * Si ya está en pantalla completa, la desactiva.
     */
    const handleFullScreen = () => {
        const elem = document.documentElement;

        if (!document.fullscreenElement) {
            elem.requestFullscreen()
                .then(() => setIsFullScreen(true))
                .catch((err) => console.error("Error al intentar activar pantalla completa", err));
        } else {
            document.exitFullscreen()
                .then(() => setIsFullScreen(false))
                .catch((err) => console.error("Error al intentar salir de pantalla completa", err));
        }
    };

    /**
     * Cambia el idioma de la aplicación.
     * @param language - Código del idioma ('en', 'es' o 'eu').
     */
    const changeLanguage = (language: 'en' | 'es' | 'eu') => {
        i18n.changeLanguage(language);
    };

    const [selectedLanguage, setSelectedLanguage] = useState(() => {
        return Cookies.get('language') || 'es';
    });

    /**
     * Cambia el idioma seleccionado y actualiza el estado del idioma.
     * @param language - Código del idioma ('en', 'es' o 'eu').
     */
    const handleLanguageChange = (language: 'en' | 'es' | 'eu') => {
        Cookies.set('language', language);
        setSelectedLanguage(language);
        changeLanguage(language);
    };

    /**
     * Aplica o quita la clase `dark-theme` al `<body>` según el estado `darkMode`.
     */
    useEffect(() => {
        if (darkMode) {
            document.body.classList.add('dark-theme');
        } else {
            document.body.classList.remove('dark-theme');
        }
    }, [darkMode]);

    /**
     * Hook de efecto que verifica si existe un token en las cookies del navegador.
     * Si el token no existe, redirige al usuario a la página login ("/").
     */
    useEffect(() => {
        const checkToken = () => {
            const token = Cookies.get('token');
            if (!token) {
                navigate('/');
            }
        };

        checkToken();

        const interval = setInterval(checkToken, 5000);

        return () => clearInterval(interval);
    }, [navigate]);

    /**
     * Maneja los clics fuera del menú de usuario para cerrarlo automáticamente.
     */
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuUserRef.current && !menuUserRef.current.contains(event.target as Node)) {
                setIsMenuUserOpen(false);
            }
        };

        if (isMenuUserOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isMenuUserOpen]);

    /**
     * Maneja los clics fuera del menú de selección de idioma para cerrarlo automáticamente.
     */
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuLanguageRef.current && !menuLanguageRef.current.contains(event.target as Node)) {
                setIsMenuLanguageOpen(false);
            }
        };

        if (isMenuLanguageOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isMenuLanguageOpen]);

    /**
     * Maneja los clics fuera del menú lateral para cerrarlo automáticamente si se está en vista móvil.
     * Además, guarda en cookies el estado del menú.
     */
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node) && window.innerWidth < 1201) {
                closeAllSubmenu();
                Cookies.set('menu-open', 'false');
            }
        };

        if (isMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isMenuOpen]);

    /**
     * Verifica si debe mostrarse un aviso para girar el teléfono (en móviles).
     * Usa una cookie para determinar si el aviso debe activarse.
     * El aviso se oculta automáticamente después de 5 segundos.
     */
    useEffect(() => {
        const rotatePhone = Cookies.get('rotate-phone');

        if (rotatePhone === '1') {
            setShowRotateWarning(true);
        } else {
            setShowRotateWarning(false);
        }

        const timer = setTimeout(() => {
            setShowRotateWarning(false);
            Cookies.set('rotate-phone', `0`);
        }, 5000);

        return () => clearTimeout(timer);
    }, []);

    const [showModalEdit, setShowModalEdit] = useState<boolean>(false);
    const [loadingBtn, setLoadingBtn] = useState(false);

    const [modalID, setModalID] = useState('');
    const [modalUsername, setModalUsername] = useState('');
    const [modalFirstName, setModalFirstName] = useState('');
    const [modalLastName, setModalLastName] = useState('');
    const [modalPhoneNumber, setModalPhoneNumber] = useState('');
    const [modalPhoneExtension, setModalPhoneExtension] = useState('');
    const [modalCountryCode, setModalCountryCode] = useState<string>("+");
    const [modalEmail, setModalEmail] = useState('');
    const [modalPassword, setModalPassword] = useState('');
    const [modalConfirmPassword, setModalConfirmPassword] = useState('');
    const [modalCompany, setModalCompany] = useState('');
    const [modalVessel, setModalVessel] = useState('');

    const [errorUsername, setErrorUsername] = useState('');
    const [errorFirstName, setErrorFirstName] = useState('');
    const [errorLastName, setErrorLastName] = useState('');
    const [errorEmail, setErrorEmail] = useState('');
    const [errorCountryCode, setErrorCountryCode] = useState('');
    const [errorPhoneNumber, setErrorPhoneNumber] = useState('');
    const [errorPhoneExtension, setErrorPhoneExtension] = useState('');
    const [errorPassword, setErrorPassword] = useState('');
    const [errorConfirmPassword, setErrorConfirmPassword] = useState('');
    const [errorNempresa, setErrorNempresa] = useState('');
    const [errorNembarcacion, setErrorNembarcacion] = useState('');

    const closeModal = () => {
        setShowModalEdit(false);
        setModalID('');
        setModalUsername('');
        setModalFirstName('');
        setModalLastName('');
        setModalPhoneExtension('');
        setModalPhoneNumber('');
        setModalCountryCode('');
        setModalEmail('');
        setModalPassword('');
        setModalCompany('');
        setModalVessel('');
        setErrorUsername('');
        setErrorFirstName('');
        setErrorLastName('');
        setErrorEmail('');
        setErrorCountryCode('');
        setErrorPhoneNumber('');
        setErrorPhoneExtension('');
        setErrorPassword('');
        setErrorConfirmPassword('');
        setErrorNempresa('');
        setErrorNembarcacion('');
        setLoadingBtn(false);
    };

    const handleOverlayClick = (e: React.MouseEvent) => {
        if ((e.target as HTMLElement).classList.contains('modalOverlay')) {
            closeModal();
        }
    };

    const extractErrorMessage = (field: any) => {
        if (Array.isArray(field)) return field[0];
        if (typeof field === 'string') return field;
        return '';
    };

    const handleUpdateUser = async () => {
        setLoadingBtn(true);

        if (!modalID || !modalUsername || !modalFirstName || !modalLastName || !modalEmail || !modalPassword || !modalConfirmPassword) {
            setErrorUsername(!modalUsername ? "Por favor complete este campo" : "");
            setErrorFirstName(!modalFirstName ? "Por favor complete este campo" : "");
            setErrorLastName(!modalLastName ? "Por favor complete este campo" : "");
            setErrorEmail(!modalEmail ? "Por favor complete este campo" : "");
            setErrorPassword(!modalPassword ? "Por favor complete este campo" : "");
            setErrorConfirmPassword(!modalConfirmPassword ? "Por favor complete este campo" : "");
            toast.error(`${t('users.pleaseCompleteAllFields')}.`);
            setLoadingBtn(false);
            return;
        }

        if (modalPassword !== modalConfirmPassword) {
            setErrorPassword("Contraseñas no coinciden");
            setErrorConfirmPassword("Contraseñas no coinciden");
            toast.error(`${t('users.thePasswordsAreNotTheSame')}.`);
            setLoadingBtn(false);
            return;
        }

        let number;

        if (!modalPhoneExtension && modalPhoneNumber && modalCountryCode) {
            number = modalCountryCode + " " + modalPhoneNumber
        } else if (modalPhoneExtension && modalPhoneNumber && modalCountryCode) {
            number = modalCountryCode + " " + modalPhoneNumber + "ext" + modalPhoneExtension
        } else {
            number = null
        }

        try {
            const companyMatch = dataCompany.find(item => item.Tempresa === modalCompany) || null;
            const vesselMatch = dataVessel.find(item => item.Tembarcacion === modalVessel) || null;

            const body = {
                id: modalID,
                username: modalUsername,
                email: modalEmail,
                firstname: modalFirstName,
                lastname: modalLastName,
                Tcontacto: number,
                Nempresa: modalCompany ? companyMatch?.Nempresa || null : null,
                Nembarcacion: modalVessel ? vesselMatch?.Nembarcacion || null : null,
                ...(modalPassword ? { password: modalPassword } : {})
            };

            const response = await patchUpdateUser(body);

            if (response.status >= 200 && response.status < 300) {
                toast.success(`${response.data.message}`);
                setLoading(true);
                getInformation();
                closeModal();
            } else {
                toast.error(`${t('users.theUserWithIDCouldNotBeEdited')}: ${modalID}`);
                setLoading(false);
                setLoadingBtn(false);
            }

        } catch (error: any) {
            let message = `${t('users.errorMakingTheRequest')}: ${error}`;

            if (axios.isAxiosError(error) && error.response?.data) {
                const data = error.response.data;

                if (typeof data === 'string') {
                    message = data;
                } else if (typeof data === 'object') {
                    const firstKey = Object.keys(data)[0];
                    const value = data[firstKey];

                    if (Array.isArray(value)) {
                        message = value[0];
                    } else {
                        message = value;
                    }
                }
            } else if (error instanceof Error) {
                message = error.message;
            }

            const errorData = error?.response?.data?.error || {};
            setErrorUsername(extractErrorMessage(errorData.username));
            setErrorFirstName(extractErrorMessage(errorData.first_name));
            setErrorLastName(extractErrorMessage(errorData.last_name));
            setErrorPhoneNumber(extractErrorMessage(errorData.Tcontacto));
            setErrorEmail(extractErrorMessage(errorData.email));
            setErrorPassword(extractErrorMessage(errorData.password));
            setErrorNempresa(extractErrorMessage(errorData.Nempresa));
            setErrorNembarcacion(extractErrorMessage(errorData.Nembarcacion));

            toast.error(`${message}`, {
                position: "top-right",
            });

            setLoading(false);
            setLoadingBtn(false);
        }
    };

    return (
        <div className="main-container">

            <div ref={menuRef} className={`menu ${isMenuOpen ? 'open' : 'closed'}`}>
                <div className='sideMenu'>
                    <div className='logo1'>
                        {isMenuOpen && (
                            <>
                                <img src={Logo1} alt="Monaguillos IoT" className='logo1-image' />
                            </>
                        )}
                        {!isMenuOpen && (
                            <img src={Logo3} alt="Monaguillos IoT" className='logo1-image' />
                        )}
                    </div>

                    <ul>
                        {isMenuOpen && (
                            <li className='caption'>MENU</li>
                        )}

                        {ability.can('read', 'AdminPage') && (
                            <li className={`item ${location.pathname === '/SmartPort/Home' ? 'active' : ''}`}
                                onClick={() => {
                                    if (isMenuOpen === true) {
                                        closeAllSubmenu();
                                    }
                                    navigate('/SmartPort/Home');
                                }}
                            >
                                <div className='menu-item'>
                                    <div>
                                        <span className='icon'><i className="fas fa-house-chimney"></i></span>
                                    </div>
                                    {isMenuOpen && (
                                        <div>
                                            <span className='text'>{t('menu.home')}</span>
                                        </div>
                                    )}
                                </div>
                            </li>
                        )}

                        <li className={`item ${location.pathname === '/SmartPort/Company' ? 'active' : ''}`}
                            onClick={() => {
                                if (isMenuOpen === true) {
                                    closeAllSubmenu();
                                }
                                navigate('/SmartPort/Company');
                            }}
                        >
                            <div className='menu-item'>
                                <div>
                                    <span className='icon'><i className="fas fa-building"></i></span>
                                </div>
                                {isMenuOpen && (
                                    <div>
                                        <span className='text'>{t('menu.company')}</span>
                                    </div>
                                )}
                            </div>
                        </li>

                        {ability.cannot('read', 'UserPage') && (
                            <li className={`item ${location.pathname === '/SmartPort/Vessels' ? 'active' : ''}`}
                                onClick={() => {
                                    if (isMenuOpen === true) {
                                        closeAllSubmenu();
                                    }
                                    navigate('/SmartPort/Vessels');
                                }}
                            >
                                <div className='menu-item'>
                                    <div>
                                        <span className='icon'><i className="fas fa-anchor"></i></span>
                                    </div>
                                    {isMenuOpen && (
                                        <div>
                                            <span className='text'>{t('menu.vessels')}</span>
                                        </div>
                                    )}
                                </div>
                            </li>
                        )}

                        {ability.can('read', 'AdminPage') && (
                            <li className={`item ${location.pathname === '/SmartPort/Incidents' ? 'active' : ''}`}
                                onClick={() => {
                                    if (isMenuOpen === true) {
                                        closeAllSubmenu();
                                    }
                                    navigate('/SmartPort/Incidents');
                                }}
                            >
                                <div className='menu-item'>
                                    <div>
                                        <span className='icon'><i className="fas fa-exclamation-triangle"></i></span>
                                    </div>
                                    {isMenuOpen && (
                                        <div>
                                            <span className='text'>{t('menu.incidents')}</span>
                                        </div>
                                    )}
                                </div>
                            </li>
                        )}

                        <li className={`item ${location.pathname === '/SmartPort/Reports' ? 'active' : ''}`}
                            onClick={() => {
                                if (isMenuOpen === true) {
                                    closeAllSubmenu();
                                }
                                navigate('/SmartPort/Reports');
                            }}
                        >
                            <div className='menu-item'>
                                <div>
                                    <span className='icon'><i className="fas fa-file-invoice"></i></span>
                                </div>
                                {isMenuOpen && (
                                    <div>
                                        <span className='text'>{t('menu.reports')}</span>
                                    </div>
                                )}
                            </div>
                        </li>

                        <li className={`item ${location.pathname === '/SmartPort/GridStatus' ? 'active' : ''}`}
                            onClick={() => {
                                if (isMenuOpen === true) {
                                    closeAllSubmenu();
                                }
                                navigate('/SmartPort/GridStatus');
                            }}
                        >
                            <div className='menu-item'>
                                <div>
                                    <span className='icon'><i className="fas fa-th"></i></span>
                                </div>
                                {isMenuOpen && (
                                    <div>
                                        <span className='text'>{t('menu.gridStatus')}</span>
                                    </div>
                                )}
                            </div>
                        </li>

                        {/* <li className={`item ${location.pathname === '/SmartPort/Registers' ? 'active' : ''}`}
                            onClick={() => {
                                if (isMenuOpen === true) {
                                    closeAllSubmenu();
                                }
                                navigate('/SmartPort/Registers');
                            }}
                        >
                            <div className='menu-item'>
                                <div>
                                    <span className='icon'><i className="fas fa-file-alt"></i></span>
                                </div>
                                {isMenuOpen && (
                                    <div>
                                        <span className='text'>{t('menu.registers')}</span>
                                    </div>
                                )}
                            </div>
                        </li> */}

                        <li className={`item ${location.pathname === '/SmartPort/Energy' ? 'active' : ''}`}
                            onClick={() => {
                                if (isMenuOpen === true) {
                                    closeAllSubmenu();
                                }
                                navigate('/SmartPort/Energy');
                            }}
                        >
                            <div className='menu-item'>
                                <div>
                                    <span className='icon'><i className="fas fa-bolt"></i></span>
                                </div>
                                {isMenuOpen && (
                                    <div>
                                        <span className='text'>{t('menu.energy')}</span>
                                    </div>
                                )}
                            </div>
                        </li>

                        <li className={`item ${location.pathname === '/SmartPort/Emissions' ? 'active' : ''}`}
                            onClick={() => {
                                if (isMenuOpen === true) {
                                    closeAllSubmenu();
                                }
                                navigate('/SmartPort/Emissions');
                            }}
                        >
                            <div className='menu-item'>
                                <div>
                                    <span className='icon'><i className="fas fa-leaf"></i></span>
                                </div>
                                {isMenuOpen && (
                                    <div>
                                        <span className='text'>{t('menu.emissions')}</span>
                                    </div>
                                )}
                            </div>
                        </li>
                    </ul>

                    <div className='logo2'>
                        {isMenuOpen && (
                            <img src={Logo2} alt="Monaguillos IoT" className='logo2-image' />
                        )}
                        {!isMenuOpen && (
                            <img src={Logo4} alt="Monaguillos IoT" className='logo2-image' />
                        )}
                    </div>
                </div>
            </div>

            <div className={`sub-container ${isMenuOpen ? 'open' : 'closed'}`}>
                <div className={`navigation-bar ${isMenuOpen ? 'open' : 'closed'}`}>
                    <div className='bar-1' onClick={() => {
                        closeAllSubmenu();
                        setIsMenuOpen(() => {
                            Cookies.set('menu-open', String(!isMenuOpen));
                            return !isMenuOpen;
                        });
                    }}>
                        <span className="icon">
                            <i className="fas fa-bars"></i>
                        </span>
                    </div>
                    <div className='bar-2'>
                        <div className='userRole'>
                            <span>{t(`menu.${userRole.toLowerCase()}`)}</span>
                        </div>

                        <div className="language-menu"
                            onClick={() => {
                                setIsMenuLanguageOpen(!isMenuLanguageOpen);
                            }}
                        >
                            {selectedLanguage === 'en' && (
                                <>
                                    <ReactCountryFlag countryCode="GB" svg className="flag-icon" />
                                </>
                            )}
                            {selectedLanguage === 'es' && (
                                <>
                                    <ReactCountryFlag countryCode="ES" svg className="flag-icon" />
                                </>
                            )}
                            {selectedLanguage === 'eu' && (
                                <>
                                    <img src="https://upload.wikimedia.org/wikipedia/commons/2/2d/Flag_of_the_Basque_Country.svg"
                                        alt="Ikurriña"
                                        className="flag-icon"
                                        width={20} />
                                </>
                            )}
                        </div>

                        <span className="icon" onClick={handleFullScreen}>
                            <i className={isFullScreen ? "fas fa-compress" : "fas fa-expand"}></i>
                        </span>

                        <span className="icon"
                            onClick={() => {
                                setDarkMode(prevMode => {
                                    const newMode = !prevMode;
                                    Cookies.set('dark-theme', String(newMode));
                                    return newMode;
                                });
                                toggleTheme();
                            }}
                        >
                            {darkMode ? (
                                <i className="fas fa-sun"></i>
                            ) : (
                                <i className="fas fa-moon"></i>
                            )}
                        </span>

                        <div className="user-menu"
                            onClick={() => {
                                setIsMenuUserOpen(!isMenuUserOpen);
                            }}
                        >
                            <div className="user-initial-circle">
                                <span>{userInitial}</span>
                            </div>
                            <div>
                                <div className="user-name">
                                    <span>{`${userFullName.split(' ')[0]} ${userLastInitial}.`}</span>
                                </div>
                                <div className="menu-arrow">
                                    <span>{isMenuUserOpen ? <i className="fas fa-chevron-up"></i> : <i className="fas fa-chevron-down"></i>}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div ref={menuLanguageRef} className={`dropdown-menu-language ${isMenuLanguageOpen ? 'open' : 'closed'}`}>
                        <div onClick={() => handleLanguageChange('en')}>
                            <ReactCountryFlag countryCode="GB" svg className='flag-icon' />
                            <span>{t('menuLanguage.english')}</span>
                        </div>
                        <div onClick={() => handleLanguageChange('es')}>
                            <ReactCountryFlag countryCode="ES" svg className='flag-icon' />
                            <span>{t('menuLanguage.spanish')}</span>
                        </div>
                        <div onClick={() => handleLanguageChange('eu')}>
                            <img src="https://upload.wikimedia.org/wikipedia/commons/2/2d/Flag_of_the_Basque_Country.svg"
                                alt="Ikurriña"
                                className="flag-icon"
                                width={20} />
                            <span>{t('menuLanguage.basque')}</span>
                        </div>
                    </div>
                    <div ref={menuUserRef} className={`dropdown-menu-user ${isMenuUserOpen ? 'open' : 'closed'}`}>
                        <ul>
                            <span>{t('menuUser.welcome')}</span>
                            {(ability.can('read', 'AdminPage')) && (
                                <>
                                    <li onClick={() => {
                                        setIsMenuUserOpen(!isMenuUserOpen);
                                        navigate('/SmartPort/Users');
                                    }}>{t('menuUser.users')}</li>
                                </>
                            )}
                            {(ability.cannot('read', 'AdminPage')) && (
                                <>
                                    <li onClick={() => {
                                        setIsMenuUserOpen(!isMenuUserOpen);
                                        setShowModalEdit(true);

                                        setModalID(idUser.toString());
                                        const selectedItem = dataProfile.find((data) => data.id === Number(idUser));
                                        setModalUsername(selectedItem ? selectedItem.user.username : "");
                                        setModalFirstName(selectedItem ? selectedItem.user.first_name : "");
                                        setModalLastName(selectedItem ? selectedItem.user.last_name : "");
                                        const contacto = selectedItem?.Tcontacto ?? "";
                                        const firstPart = contacto.split(' ')[0] || "";
                                        const extIndex = contacto.toLowerCase().indexOf("ext");
                                        const secondPart = extIndex !== -1 ? contacto.substring(contacto.indexOf(" ") + 1, extIndex).trim() : contacto.split(' ')[1] || "";
                                        setModalCountryCode(firstPart);
                                        setModalPhoneNumber(secondPart);
                                        const thirdPart = extIndex !== -1 ? contacto.substring(extIndex + 3).trim() : "";
                                        setModalPhoneExtension(thirdPart);
                                        setModalEmail(selectedItem ? selectedItem.user.email : "");
                                        const companyMatch = dataCompany.find(item => item.Nempresa === selectedItem?.Nempresa?.Nempresa);
                                        const vesselMatch = dataVessel.find(item => item.Nembarcacion === selectedItem?.Nembarcacion?.Nembarcacion);
                                        setModalCompany(companyMatch ? companyMatch.Tempresa : "");
                                        setModalVessel(vesselMatch ? vesselMatch.Tembarcacion.toString() : "");
                                    }}>{t('menuUser.changePassword')}</li>
                                </>
                            )}
                            <div className='line'></div>
                            <li onClick={() => {
                                const cookiesToKeep = ['menu-open', 'language', 'dark-theme']
                                Object.keys(Cookies.get()).forEach(cookieName => {
                                    if (!cookiesToKeep.includes(cookieName)) {
                                        Cookies.remove(cookieName);
                                    }
                                });
                                localStorage.clear();
                                navigate('/');
                            }}>{t('menuUser.logOut')}</li>
                        </ul>
                    </div>
                </div>

                <div className="horizontal-line"></div>

                <div className='body'>
                    {loading ? (
                        <div className="loading">
                            <ClipLoader color="#007bff" size={50} />
                            <p>{t('incidents.loadingInformation')}</p>
                        </div>
                    ) : (
                        <>
                            <Outlet />
                        </>
                    )}

                    {showRotateWarning && (
                        <div className="rotate-warning">
                            <img src="./images/rotate-phone.gif" alt="Rotate phone" className="rotate-gif" />
                            <h3>{t('main.rotateYourDevice')}</h3>
                            <p>{t('main.pleaseRotateYourDeviceToLandscapeModeToUseThisApp')}.</p>
                        </div>
                    )}

                    {(showModalEdit) && (
                        <div className='modalOverlay' onClick={handleOverlayClick}>
                            <div className='modalContent'>
                                <div className='title'>
                                    <h6>
                                        {showModalEdit ? t('menuUser.changePassword') : ''}
                                    </h6>
                                    <span className="closeIcon" onClick={() => {
                                        closeModal();
                                    }}>&times;</span>
                                </div>
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6>ID</h6>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={modalID}
                                            placeholder="Search by ID"
                                            disabled
                                        />
                                    </div>
                                </div>
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6><span>*</span> {t('users.username')}</h6>
                                        <input
                                            type="text"
                                            className={`form-control ${errorUsername ? 'is-invalid' : ''}`}
                                            value={modalUsername}
                                            onChange={(e) => {
                                                setModalUsername(e.target.value);
                                                setErrorUsername('');
                                            }}
                                            placeholder={`${t('users.enterYour')} ${t('users.username').toLowerCase()}`}
                                            disabled
                                        />
                                    </div>
                                    {errorUsername && (
                                        <div className="invalid-feedback d-block">{errorUsername}</div>
                                    )}
                                </div>
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6><span>*</span> {t('users.name')}</h6>
                                        <input
                                            type="text"
                                            className={`form-control ${errorFirstName ? 'is-invalid' : ''}`}
                                            value={modalFirstName}
                                            onChange={(e) => {
                                                setModalFirstName(e.target.value);
                                                setErrorFirstName('');
                                            }}
                                            placeholder={`${t('users.enterYour')} ${t('users.name').toLowerCase()}`}
                                        />
                                    </div>
                                    {errorFirstName && (
                                        <div className="invalid-feedback d-block">{errorFirstName}</div>
                                    )}
                                </div>
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6><span>*</span> {t('users.lastName')}</h6>
                                        <input
                                            type="text"
                                            className={`form-control ${errorLastName ? 'is-invalid' : ''}`}
                                            value={modalLastName}
                                            onChange={(e) => {
                                                setModalLastName(e.target.value);
                                                setErrorLastName('');
                                            }}
                                            placeholder={`${t('users.enterYour')} ${t('users.lastName').toLowerCase()}`}
                                        />
                                    </div>
                                    {errorLastName && (
                                        <div className="invalid-feedback d-block">{errorLastName}</div>
                                    )}
                                </div>
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6> {t('users.numberContact')}</h6>
                                        <div className='phone'>
                                            <div className="form-control phone-country">
                                                <PhoneInput
                                                    defaultCountry="ES"
                                                    international
                                                    countryCallingCodeEditable={false}
                                                    value={modalCountryCode}
                                                    onChange={(value) => {
                                                        setErrorCountryCode("");
                                                        if (value) {
                                                            let temp;
                                                            if (!value.startsWith("+")) {
                                                                temp = "+" + value.replace(/\D/g, "").slice(0, 4);
                                                            } else {
                                                                temp = "+" + value.slice(1).replace(/\D/g, "").slice(0, 4);
                                                            }
                                                            setModalCountryCode(temp);
                                                        } else {
                                                            setModalCountryCode("+");
                                                        }
                                                    }}
                                                    readOnly={false}
                                                />
                                            </div>
                                            <input
                                                type="text"
                                                className={`form-control ${errorPhoneNumber ? 'is-invalid' : ''}`}
                                                placeholder={t('users.number')}
                                                value={modalPhoneNumber}
                                                onChange={(e) => {
                                                    const onlyNumbers = e.target.value.replace(/[^0-9]/g, "");
                                                    setModalPhoneNumber(onlyNumbers);
                                                    setErrorPhoneNumber("");
                                                }}
                                            />
                                            <input
                                                type="text"
                                                className={`form-control ${errorPhoneExtension ? 'is-invalid' : ''}`}
                                                placeholder="Ext"
                                                value={modalPhoneExtension}
                                                onChange={(e) => {
                                                    const onlyNumbers = e.target.value.replace(/[^0-9]/g, "");
                                                    setModalPhoneExtension(onlyNumbers);
                                                    setErrorPhoneExtension("");
                                                }}
                                            />
                                        </div>
                                        {(errorCountryCode || errorPhoneNumber || errorPhoneExtension) && (
                                            <div className="invalid-feedback d-block">{(errorCountryCode || errorPhoneNumber || errorPhoneExtension)}</div>
                                        )}
                                    </div>
                                </div>
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6><span>*</span> {t('users.email')}</h6>
                                        <input
                                            type="text"
                                            className={`form-control ${errorEmail ? 'is-invalid' : ''}`}
                                            value={modalEmail}
                                            onChange={(e) => {
                                                setModalEmail(e.target.value);
                                                setErrorEmail('');
                                            }}
                                            placeholder={`${t('users.enterYour')} ${t('users.email').toLowerCase()}`}
                                            disabled
                                        />
                                    </div>
                                    {errorEmail && (
                                        <div className="invalid-feedback d-block">{errorEmail}</div>
                                    )}
                                </div>
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6><span>*</span> {t('users.password')}</h6>
                                        <input
                                            type="password"
                                            className={`form-control ${errorPassword ? 'is-invalid' : ''}`}
                                            value={modalPassword}
                                            onChange={(e) => {
                                                setModalPassword(e.target.value);
                                                setErrorPassword('');
                                            }}
                                            placeholder={`${t('users.enterYour')} ${t('users.password').toLowerCase()}`}
                                            onCopy={(e) => e.preventDefault()}
                                            onPaste={(e) => e.preventDefault()}
                                            onCut={(e) => e.preventDefault()}
                                        />
                                    </div>
                                    {errorPassword && (
                                        <div className="invalid-feedback d-block">{errorPassword}</div>
                                    )}
                                </div>
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6><span>*</span> {t('users.confirmPassword')}</h6>
                                        <input
                                            type="password"
                                            className={`form-control ${errorConfirmPassword ? 'is-invalid' : ''}`}
                                            value={modalConfirmPassword}
                                            onChange={(e) => {
                                                setModalConfirmPassword(e.target.value);
                                                setErrorConfirmPassword('');
                                            }}
                                            placeholder={`${t('users.enterYour')} ${t('users.password').toLowerCase()} ${t('users.again')}`}
                                            onCopy={(e) => e.preventDefault()}
                                            onPaste={(e) => e.preventDefault()}
                                            onCut={(e) => e.preventDefault()}
                                        />
                                    </div>
                                    {errorConfirmPassword && (
                                        <div className="invalid-feedback d-block">{errorConfirmPassword}</div>
                                    )}
                                </div>
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6>{t('users.company')}</h6>
                                        <div className="select-wrapper">
                                            <select
                                                className={`form-control ${errorNempresa ? 'is-invalid' : ''}`}
                                                value={modalCompany}
                                                disabled
                                            >
                                                <option value="">{t('users.selectACompany')} ...</option>
                                                {dataCompany
                                                    .map((item) => (
                                                        <option key={item.Nempresa} value={String(item.Tempresa)}>
                                                            {item.Tempresa}
                                                        </option>
                                                    ))}
                                            </select>
                                            <span className="icon">
                                                {false ? (
                                                    <i className="fas fa-chevron-up"></i>
                                                ) : (
                                                    <i className="fas fa-chevron-down"></i>
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                    {errorNempresa && (
                                        <div className="invalid-feedback d-block">{errorNempresa}</div>
                                    )}
                                </div>
                                {(ability.cannot('read', 'ManagerPage')) && (
                                    <div className='formInput'>
                                        <div className="input-row">
                                            <h6>{t('users.vessel')}</h6>
                                            <div className="select-wrapper">
                                                <select
                                                    className={`form-control ${errorNembarcacion ? 'is-invalid' : ''}`}
                                                    value={modalVessel}
                                                    disabled
                                                >
                                                    <option value="">{t('users.selectAVessel')} ...</option>
                                                    {dataVessel
                                                        .map((item) => (
                                                            <option key={item.Nembarcacion} value={String(item.Tembarcacion)}>
                                                                {item.Tembarcacion}
                                                            </option>
                                                        ))}
                                                </select>
                                                <span className="icon">
                                                    {false ? (
                                                        <i className="fas fa-chevron-up"></i>
                                                    ) : (
                                                        <i className="fas fa-chevron-down"></i>
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                        {errorNembarcacion && (
                                            <div className="invalid-feedback d-block">{errorNembarcacion}</div>
                                        )}
                                    </div>
                                )}
                                <div className='formBtn'>
                                    <button className={'btn btn-dark'} onClick={() => {
                                        handleUpdateUser();
                                    }}
                                        disabled={loadingBtn}>
                                        {loadingBtn ? (
                                            <div className="loading-btn">
                                                <ClipLoader color="#007bff" size={20} />
                                            </div>
                                        ) : (
                                            <h6>
                                                {showModalEdit ? t('users.updateUser') : ''}
                                            </h6>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="horizontal-line"></div>

                <div className='footer'>
                    {window.innerWidth > 500 && (
                        <div className='image'></div>
                    )}
                    <div className='title'>
                        <span>
                            By <a href="https://www.euskabea.com" target="_blank" rel="noopener noreferrer">Euskabea</a> - <a href="https://satiins.es" target="_blank" rel="noopener noreferrer">Satiins</a>
                        </span>

                    </div>
                    <div className='image'>
                        <img src={Footer1} alt="Monaguillos IoT" className='footer-image' />
                        <img src={Footer2} alt="Monaguillos IoT" className='footer-image' />
                        <img src={Footer3} alt="Monaguillos IoT" className='footer-image' />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PreMain;